import { canAddMlSheetShipments, isContractPartyAdmin, isPickupMan } from './networkMode.js';

export const isAgentRole = (user) => user?.role === 'Agent';

export const isContractPartyAdminRole = (user) => isContractPartyAdmin(user);

export const isUserOnMlSheetAccessList = (sheet, userId) =>
  (sheet?.access_users || []).some((accessUser) => {
    if (typeof accessUser === 'string') return accessUser === userId;
    return (
      accessUser?.user_id === userId ||
      accessUser?.id === userId ||
      accessUser === userId
    );
  });

/** Agent may view / update status only if they created the sheet or are on access_users. */
export const canAgentManageMlSheet = (sheet, user) => {
  if (!user || !isAgentRole(user)) return true;
  if (sheet?.can_update_status != null) return Boolean(sheet.can_update_status);
  if (sheet?.created_by === user.user_id) return true;
  return isUserOnMlSheetAccessList(sheet, user.user_id);
};

export const canAgentViewMlSheet = (sheet, user) => canAgentManageMlSheet(sheet, user);

export const canAgentUpdateMlSheetStatus = (sheet, user) => canAgentManageMlSheet(sheet, user);

/** Contract Party Admin: view/update status when backend sets can_update_status. */
export const canContractPartyAdminViewMlSheet = (sheet, user) => {
  if (!isContractPartyAdminRole(user)) return true;
  return sheet?.can_update_status != null ? Boolean(sheet.can_update_status) : true;
};

export const canContractPartyAdminUpdateMlSheetStatus = (sheet, user) => {
  if (!isContractPartyAdminRole(user)) return false;
  if (sheet?.can_update_status != null) return Boolean(sheet.can_update_status);
  return false;
};

export const canViewMlSheet = (sheet, user) => {
  if (!user) return false;
  if (isContractPartyAdminRole(user)) return canContractPartyAdminViewMlSheet(sheet, user);
  if (isPickupMan(user)) return true;
  if (isAgentRole(user)) return canAgentViewMlSheet(sheet, user);
  return true;
};

export const canAppendShipmentsOnMlSheet = (sheet, user) => {
  if (!canAddMlSheetShipments(user) || !canViewMlSheet(sheet, user)) return false;
  if (String(sheet?.status || '').toUpperCase() === 'CLOSE') return false;
  return !(sheet?.edit_blockers || []).length;
};

export const canUpdateMlSheetStatus = (sheet, user) => {
  if (!user) return false;
  if (['System Admin', 'Admin', 'Accountant'].includes(user.role)) return true;
  if (isContractPartyAdminRole(user)) return canContractPartyAdminUpdateMlSheetStatus(sheet, user);
  if (isAgentRole(user)) return canAgentManageMlSheet(sheet, user);
  if (sheet?.can_update_status != null) return Boolean(sheet.can_update_status);
  return false;
};

const ML_FINANCE_ROLES = ['System Admin', 'Admin', 'Accountant', 'Hub Admin'];

/** Assigned access user (not admin/accountant) — hide totals and gate PDF. */
export const isAssignedMlAccessViewer = (sheet, user) => {
  if (!user || !sheet) return false;
  if (ML_FINANCE_ROLES.includes(user.role)) return false;
  if (isContractPartyAdminRole(user)) return true;
  if (sheet.hide_amounts != null) return Boolean(sheet.hide_amounts);
  return isUserOnMlSheetAccessList(sheet, user.user_id);
};

export const hideMlSheetAmounts = (sheet, user) => isAssignedMlAccessViewer(sheet, user);

/** Dispatch list keeps a Total Amount column for every row (agents included). */
export const showMlSheetListAmount = (user) => !isContractPartyAdminRole(user);

export const mlSheetHasOutstandingToPay = (sheet, shipments = []) => {
  if (sheet?.outstanding_to_pay != null) return Number(sheet.outstanding_to_pay) > 0;
  if (Number(sheet?.summary?.total_balance) > 0) return true;
  if (Number(sheet?.total_to_pay) > 0) return true;
  return (shipments || []).some((s) => Number(s.balance_amount) > 0);
};

export const isPaidSettlementEnforced = (user) =>
  user?.require_paid_settlement_before_ml !== false;

export const isToPayLockEnforced = (user) =>
  user?.lock_ml_shipment_details_until_remit !== false;

export const canDownloadMlSheetPdf = (sheet, user, shipments = []) => {
  // Agents always get print and download. The remit lock only hides shipment lines.
  if (isAgentRole(user)) return true;
  if (!isToPayLockEnforced(user)) return true;
  if (sheet?.can_download_pdf != null) return Boolean(sheet.can_download_pdf);
  if (!isAssignedMlAccessViewer(sheet, user)) return true;
  return !mlSheetHasOutstandingToPay(sheet, shipments);
};
